import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import SuiBox from "components/SuiBox";
import SuiInput from "components/SuiInput";

import Card from "@mui/material/Card";

import toast from "react-hot-toast";
import moment from "moment";

import Prompt from "components/UpStyled/Prompt";
import api from "services/api";
import { nanoid } from "nanoid";
import { AccountContext } from "context/account";

import EventCalendar from "examples/Calendar";
import AddEventModal from "pages/de/dashboard/components/AddEventModal";
import EventModalViewer from "pages/de/dashboard/components/EventModalViewer";

import Flatpickr from "react-flatpickr";
import SuiTypography from "components/SuiTypography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import SuiButton from "components/SuiButton";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import AddIcon from "@mui/icons-material/Add";
import IosShareIcon from "@mui/icons-material/IosShare";
import GetAppOutlinedIcon from "@mui/icons-material/GetAppOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShareIcon from "@mui/icons-material/Share";
import BlockIcon from "@mui/icons-material/Block";

import ColorDropDownMenu from "pages/calendar/components/ColorDropDownMenu";
import CreateCalendarModal from "pages/calendar/components/CreateCalendarModal";
import ImportCalendarFileModal from "pages/calendar/components/ImportCalendarFileModal";
import ShareListModal from "pages/de/share-page/components/ShareListModal";

import { convertDbToEvent, convertEventDb } from "utils/helper";

const MyCalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [startDate, setStartDate] = useState(moment());
  const [selectedInfo, setSelectedInfo] = useState({});
  const [selectedCalendar, setSelectedCalendar] = useState({});
  const [addEventVisible, setAddEventVisible] = useState(false);
  const [editEventVisible, setEditEventVisible] = useState(false);
  const [viewEventVisible, setViewEventVisible] = useState(false);

  const [actionmenuVisible, setActionmenuVisible] = useState(null);
  const [calendarMenuVisible, setCalendarMenuVisible] = useState(null);

  const [changeColorVisible, setChangeColorVisible] = useState(null);
  const [createCalendarVisible, setCreateCalendarVisible] = useState(false);
  const [editCalendarVisible, setEditCalendarVisible] = useState(false);
  const [importCalendarVisible, setImportCalendarVisible] = useState(false);
  const [currentView, setCurrentView] = useState("Month ▾");
  const [viewMenuVisible, setViewMenuVisible] = useState(null);

  const [calendarOptions, setCalendarOptions] = useState([]); //calendars owned by you
  const [sharedCalendarOptions, setSharedCalendarOptions] = useState([]); //calendars shared to you
  const [shareVisible, setShareVisible] = useState(false);
  const [permittedUsers, setPermittedUsers] = useState([]);
  const [shareToAllDesiignersVisible, setShareToAllDesiignersVisible] = useState(false);
  const [unshareToAllDesiignersVisible, setUnshareToAllDesiignersVisible] = useState(false);

  const [checked, setChecked] = useState([]);

  const calRef = useRef(null);
  const fpRef = useRef(null);

  const account = useContext(AccountContext);

  const navigate = useNavigate();

  useEffect(() => {
    if (account.id) {
      retrieveEvents();
      setSelectedInfo({
        start: startDate.startOf("minute").toDate(),
        end: startDate.add("1", "hour").startOf("minute").toDate(),
        allDay: false,
        view: {
          calendar: calRef?.current?.getApi(),
        },
      });
    }
  }, [account.id]);

  const retrieveEvents = async (inactiveIds) => {
    try {
      //get calendar list owned by you
      const temp = await api.calendar.listCalendars({ order: "created_at" });
      const calendars = temp?.data?.calendars || [];
      const myCalendarOptions = calendars.map((x) => ({
        value: x.id,
        label: x.title,
        color: x?.setting?.color || "#abe9f7",
        setting: x?.setting,
        active: x?.active,
        owner_id: x?.owner_id,
      }));
      setCalendarOptions(myCalendarOptions);

      //get shared calendar list
      const shared = await api.calendar.sharedCalendarList({ order: "created_at" });
      let sharedCalendars = shared?.data?.calendars || [];
      const inactiveCalendars = inactiveIds
        ? inactiveIds
        : account?.metadata?.inactive_calendars || [];

      const mySharedCalendarOptions = sharedCalendars.map((x) => ({
        value: x.id,
        label: x.title,
        color: x?.setting?.color || "#abe9f7",
        setting: x?.setting,
        active: !inactiveCalendars.includes(x.id),
        owner_id: x?.owner_id,
      }));
      setSharedCalendarOptions(mySharedCalendarOptions);
      let cids = myCalendarOptions.filter((x) => x.active).map((x) => x.value);

      let scids = mySharedCalendarOptions.filter((x) => x.active).map((x) => x.value);

      setChecked([...cids, ...scids]);
      let rawEvents = [];
      let sharedRawEvents = [];

      if (cids.length > 0) {
        const { data } = await api.calendar.listEvents({ calendar_id: cids.join(":") });
        let raw = data?.events || [];
        rawEvents = raw.map((x) => convertDbToEvent(x, account.id));
      }
      if (scids.length > 0) {
        const { data } = await api.calendar.listSharedCalendarEvents({
          calendar_id: scids.join(":"),
        });
        let raw = data?.events || [];
        sharedRawEvents = raw.map((x) => convertDbToEvent(x, account.id));
      }

      setEvents([...rawEvents, ...sharedRawEvents]);
    } catch (err) {
      console.log(err);
      toast.error(`Could not retrieve calendars!`);
    }
  };

  const retrieveUsersWithPermission = async (data, type) => {
    if (!data?.id || !type) {
      return;
    }
    try {
      let payload = {
        resource_type: type,
        resource_id: data.id,
        //resource_owner_id: ownerStore?.id,
      };
      if (isOwner) {
        const rp2 = await api.resourcePermission.sharingList(payload);
        let userList2 = rp2?.data?.resource_permissions || [];
        let filteredList2 = removeDuplicate(userList2);
        setPermittedUsers(filteredList2 || []);
      } //
    } catch (err) {
      console.log(err);
    }
  };

  const handleDateClick = (e) => {
    console.log(e);
  };

  const handleDateSelect = (selectInfo) => {
    setAddEventVisible(true);
    setSelectedInfo(selectInfo);
  };

  const handleEventClick = (clickInfo) => {
    setViewEventVisible(true);

    setSelectedInfo(clickInfo);
  };

  const handleEventChange = async ({ event, oldEvent }) => {
    let payload = convertEventDb(event);
    if (!payload?.id) {
      return toast.error("Can't find the event ID!");
    }
    try {
      const { data } = await api.calendar.updateEvent(payload?.id, payload);
      const updated = data?.updated || {};
      const newEvents = [];

      events.forEach((x) => {
        if (x.id == updated.id) {
          const newEvent = convertDbToEvent(updated);
          newEvents.push(newEvent);
        } else {
          newEvents.push(x);
        }
      });
      setEvents(newEvents);
    } catch (err) {
      return toast.error("Event could not be updated!");
    }
  };

  const handleCalendarColorChange = async (color, calendar_id, setting) => {
    try {
      await api.calendar.updateCalendar(calendar_id, {
        setting: { ...setting, color: color?.value },
      });
      let newCalendarOptions = [];
      for (let i = 0; i < calendarOptions.length; i++) {
        if (calendarOptions[i]?.value == calendar_id) {
          newCalendarOptions.push({
            value: calendarOptions[i].value,
            label: calendarOptions[i].label,
            setting: {
              ...calendarOptions[i]?.setting,
              color: color?.value,
            },
            color: color?.value,
            active: calendarOptions[i].active,
          });
        } else {
          newCalendarOptions.push(calendarOptions[i]);
        }
      }

      setCalendarOptions(newCalendarOptions);
    } catch (err) {
      console.log(err);
      toast.error(`Could not update the color!`);
    }
  };

  const handleRefresh = () => {
    setSelectedCalendar({});
    retrieveEvents();
  };

  const handleToggle = async (value, isShared = false) => {
    const currentIndex = checked.findIndex((x) => x == value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);

    const isActive = currentIndex === -1 ? true : false;

    try {
      if (isShared) {
        let inactiveIds = account?.metadata?.inactive_calendars || [];
        if (isActive) {
          inactiveIds = inactiveIds.filter((x) => x != value);
        } else {
          inactiveIds.push(value);
        }

        const { data } = await account.handleUpdate(account.id, {
          metadata: { inactive_calendars: inactiveIds },
        });
        retrieveEvents(inactiveIds);
      } else {
        let payload = {
          active: isActive,
        };
        const newCreatedCalendar = await api.calendar.updateCalendar(value, payload);
        retrieveEvents();
      }
    } catch (err) {
      toast.error(`${isActive ? "Hide" : "Active"} the calendar failed!`);
      console.log(err);
    }
  };

  const removeDuplicate = (userList) => {
    let idSet = new Set();
    let filteredList = [];
    for (let i = 0; i < userList.length; i++) {
      if (idSet.has(userList[i].user_id) || !userList[i].user) {
        //
      } else {
        idSet.add(userList[i].user_id);
        filteredList.push(userList[i]);
      }
    }
    return filteredList;
  };

  const handleCloseActionMenu = () => setActionmenuVisible(null);

  const ViewActionMenu = ({}) => (
    <Menu
      id="action-menu"
      anchorEl={actionmenuVisible}
      open={Boolean(actionmenuVisible)}
      onClose={handleCloseActionMenu}
      MenuListProps={{
        "aria-labelledby": "basic-button",
      }}
    >
      {selectedCalendar.owner_id == account.id ? (
        <MenuItem
          onClick={() => {
            handleCloseActionMenu();
            setEditCalendarVisible(true);
          }}
        >
          <SuiBox display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
            <EditIcon fontSize="small" />
            <SuiTypography variant="body1" textAlign="center">
              Edit
            </SuiTypography>{" "}
          </SuiBox>
        </MenuItem>
      ) : null}
      <MenuItem
        onClick={() => {
          handleCloseActionMenu();
          api.calendar.exportCalendar(selectedCalendar.value);
        }}
      >
        <SuiBox display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
          <FileDownloadOutlinedIcon fontSize="small" />
          <SuiTypography variant="body1" textAlign="center">
            Export Calendar
          </SuiTypography>{" "}
        </SuiBox>
      </MenuItem>

      {account.role == "admin" ? (
        permittedUsers.findIndex((x) => x.user_id == "AllDesigners") > 0 ? (
          <Tooltip title={`Share this content to all designer!`} placement="left">
            <MenuItem>
              <SuiBox
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                gap={1}
                onClick={() => {
                  setUnshareToAllDesiignersVisible(true);
                  handleCloseActionMenu();
                }}
              >
                <BlockIcon fontSize="small" />

                <SuiBox>
                  <SuiTypography variant="body1" textAlign="center">
                    Unshare to All Designers
                  </SuiTypography>
                </SuiBox>
              </SuiBox>{" "}
            </MenuItem>
          </Tooltip>
        ) : (
          <Tooltip title={`Share this content to all designer!`} placement="left">
            <MenuItem>
              <SuiBox
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                gap={1}
                onClick={() => {
                  setShareToAllDesiignersVisible(true);
                  handleCloseActionMenu();
                }}
              >
                <ShareIcon fontSize="small" />

                <SuiBox>
                  <SuiTypography variant="body1" textAlign="center">
                    Share to All Designers
                  </SuiTypography>
                </SuiBox>
              </SuiBox>{" "}
            </MenuItem>
          </Tooltip>
        )
      ) : null}
    </Menu>
  );

  const handleCloseCalendarMenu = () => setCalendarMenuVisible(null);
  const CalendarMenu = () => (
    <Menu
      id="calendar-menu"
      anchorEl={calendarMenuVisible}
      open={Boolean(calendarMenuVisible)}
      onClose={handleCloseCalendarMenu}
      MenuListProps={{
        "aria-labelledby": "basic-button",
      }}
    >
      <MenuItem
        onClick={() => {
          handleCloseCalendarMenu();
          setCreateCalendarVisible(true);
        }}
      >
        <SuiBox display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
          <AddIcon fontSize="small" />
          <SuiTypography variant="body1" textAlign="center">
            Create Calendar
          </SuiTypography>{" "}
        </SuiBox>
      </MenuItem>
      <MenuItem
        onClick={() => {
          setImportCalendarVisible(true);
          handleCloseCalendarMenu();
        }}
      >
        <SuiBox display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
          <FileUploadOutlinedIcon fontSize="small" />
          <SuiTypography variant="body1" textAlign="center">
            Import Calendar
          </SuiTypography>{" "}
        </SuiBox>
      </MenuItem>
    </Menu>
  );

  const handleMonthChange = (selectedDates, dateStr, instance) => {
    const currentMonth = instance.currentMonth + 1;
    const currentYear = instance.currentYear;
    if (calRef.current) {
      const calApis = calRef.current.getApi();
      const date = moment(currentYear + "-" + currentMonth, "YYYY-M").toDate();
      calApis.gotoDate(date);
    }
  };

  const handleOnChangeDate = (selectedDates, dateStr, instance) => {
    const newDate = moment(selectedDates[0]);
    const startNewDate = newDate.startOf("minute");
    setStartDate(startNewDate);
  };

  const handleCloseViewMenu = () => setViewMenuVisible(false);

  const ActionMenu = () => (
    <Menu
      id="user-management-menu"
      anchorEl={viewMenuVisible}
      open={Boolean(viewMenuVisible)}
      onClose={handleCloseViewMenu}
      MenuListProps={{
        "aria-labelledby": "basic-button",
      }}
    >
      <MenuItem
        onClick={(e) => {
          const calApis = calRef?.current.getApi();
          calApis.changeView("timeGridDay");
          setCurrentView("Day ▾");
          handleCloseViewMenu();
        }}
      >
        <ListItemText>Day</ListItemText>
        <SuiTypography variant="body1" color="text">
          D
        </SuiTypography>{" "}
      </MenuItem>
      <MenuItem
        onClick={(e) => {
          const calApis = calRef?.current.getApi();
          calApis.changeView("timeGridWeek");
          setCurrentView("Week ▾");
          handleCloseViewMenu();
        }}
      >
        <ListItemText>Week</ListItemText>
        <SuiTypography variant="body1" color="text">
          W
        </SuiTypography>{" "}
      </MenuItem>
      <MenuItem
        onClick={(e) => {
          const calApis = calRef?.current.getApi();
          calApis.changeView("dayGridMonth");
          setCurrentView("Month ▾");
          handleCloseViewMenu();
        }}
      >
        <ListItemText>Month</ListItemText>
        <SuiTypography variant="body1" color="text">
          M
        </SuiTypography>{" "}
      </MenuItem>
      <MenuItem
        onClick={(e) => {
          const calApis = calRef?.current.getApi();
          calApis.changeView("multiMonthYear");
          setCurrentView("Year ▾");
          handleCloseViewMenu();
        }}
      >
        <ListItemText>Year</ListItemText>
        <SuiTypography variant="body1" color="text">
          Y
        </SuiTypography>{" "}
      </MenuItem>
      <MenuItem
        onClick={(e) => {
          const calApis = calRef?.current.getApi();
          calApis.changeView("listMonth");
          setCurrentView("List ▾");
          handleCloseViewMenu();
        }}
      >
        <ListItemText>List</ListItemText>
        <SuiTypography variant="body1" color="text">
          L
        </SuiTypography>{" "}
      </MenuItem>
    </Menu>
  );

  return (
    <SuiBox height="100%">
      <SuiBox display="flex" gap={2}>
        <Card sx={{ p: 2 }}>
          <SuiBox>
            <SuiBox sx={{ mb: 1 }} display="flex" justifyContent="space-between">
              <SuiButton variant="text" color="dark" onClick={() => navigate(-1)} size="large">
                <ArrowBackIcon fontSize="sm" sx={{ mr: 0.5 }} /> Back
              </SuiButton>

              <SuiButton
                size="small"
                color="dark"
                sx={{ px: 2 }}
                onClick={() => {
                  setSelectedInfo({
                    start: startDate.startOf("minute").toDate(),
                    end: startDate.add("1", "hour").startOf("minute").toDate(),
                    allDay: false,
                    view: {
                      calendar: calRef?.current?.getApi(),
                    },
                  });
                  setAddEventVisible(true);
                }}
              >
                <AddIcon fontSize="medium" sx={{ mr: 0.5 }} />
                Add Event
              </SuiButton>
            </SuiBox>
            <Flatpickr
              options={{ inline: true }}
              className="hidden"
              onMonthChange={handleMonthChange}
              onYearChange={handleMonthChange}
              onChange={handleOnChangeDate}
              onReady={(_, __, fp) => {
                fp.calendarContainer.style.display = "block";
                fp.calendarContainer.style.boxShadow = "none";
                fp.calendarContainer.style.webkitBoxShadow = "none";
                fp.calendarContainer.classList.remove("arrowTop");
              }}
              ref={fpRef}
            />
            <SuiBox sx={{ my: 1 }}>
              <SuiBox display="flex" justifyContent="space-between">
                <SuiTypography variant="h6" sx={{ mb: 1 }}>
                  My Calendars
                </SuiTypography>
                <SuiButton
                  variant="text"
                  color="dark"
                  sx={{ textTransform: "none" }}
                  onClick={(e) => setCalendarMenuVisible(e?.target)}
                >
                  More
                  <KeyboardArrowDownIcon />
                </SuiButton>
                <CalendarMenu />
              </SuiBox>
              {calendarOptions.map((item, index) => {
                return (
                  <SuiBox
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 0.5 }}
                  >
                    <SuiBox
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      sx={{ cursor: "pointer" }}
                      onClick={(e) => handleToggle(item.value)}
                    >
                      <Checkbox
                        checked={checked.find((x) => x == item.value) ? true : false}
                        disableRipple
                      />
                      <SuiTypography variant="body1">{item.label}</SuiTypography>
                    </SuiBox>
                    <SuiBox display="flex" alignItems="center" gap={0.5}>
                      <ColorDropDownMenu
                        color={{ value: item?.color, label: item?.color }}
                        onClick={(color) =>
                          handleCalendarColorChange(color, item.value, item.setting)
                        }
                      />

                      <MoreVertIcon
                        fontSize="small"
                        onClick={(e) => {
                          setSelectedCalendar(item);
                          setActionmenuVisible(e.target);
                          retrieveUsersWithPermission(item, "calendar");
                        }}
                        color="text"
                        sx={{ cursor: "pointer", mr: 1 }}
                      />
                    </SuiBox>
                  </SuiBox>
                );
              })}
            </SuiBox>
            {sharedCalendarOptions.length > 0 ? (
              <SuiBox sx={{ my: 1 }}>
                <SuiBox display="flex" justifyContent="space-between">
                  <SuiTypography variant="h6" sx={{ mb: 1 }}>
                    Shared Calendars
                  </SuiTypography>
                </SuiBox>
                {sharedCalendarOptions.map((item, index) => {
                  return (
                    <SuiBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 0.5 }}
                    >
                      <SuiBox
                        onClick={(e) => handleToggle(item.value, true)}
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{ cursor: "pointer" }}
                      >
                        <Checkbox
                          checked={checked.find((x) => x == item.value) ? true : false}
                          disableRipple
                        />
                        <SuiTypography variant="body1">{item.label}</SuiTypography>
                      </SuiBox>
                      <SuiBox display="flex" alignItems="center" gap={0.5}>
                        <ColorDropDownMenu
                          color={{ value: item?.color, label: item?.color }}
                          onClick={null}
                        />

                        <MoreVertIcon
                          fontSize="small"
                          onClick={(e) => {
                            setSelectedCalendar(item);
                            setActionmenuVisible(e.target);
                          }}
                          color="text"
                          sx={{ cursor: "pointer", mr: 1 }}
                        />
                      </SuiBox>
                    </SuiBox>
                  );
                })}
              </SuiBox>
            ) : null}
          </SuiBox>
        </Card>
        <SuiBox sx={{ flexGrow: 1, width: 500 }}>
          <EventCalendar
            initialView="dayGridMonth"
            headerToolbar={{
              left: "myCustomPrev,myCustomNext myCustomToday",
              center: "title",
              right: "viewButton",
            }}
            customButtons={{
              myCustomPrev: {
                text: "<",
                click: function () {
                  const calApi = calRef?.current?.getApi();
                  calApi.prev();
                  const currentDate = calApi.currentData.currentDate;
                  const dateStr = currentDate.toISOString().slice(0, 19);
                  fpRef.current.flatpickr.setDate(moment(dateStr).toDate());
                },
              },
              myCustomNext: {
                text: ">",
                click: function () {
                  const calApi = calRef?.current?.getApi();
                  calApi.next();
                  const currentDate = calApi.currentData.currentDate;
                  const dateStr = currentDate.toISOString().slice(0, 19);
                  fpRef.current.flatpickr.setDate(moment(dateStr).toDate());
                },
              },
              myCustomToday: {
                text: "today",
                click: function () {
                  const calApi = calRef?.current?.getApi();

                  calApi.today();
                  const currentDate = calApi.currentData.currentDate;
                  const dateStr = currentDate.toISOString().slice(0, 19);
                  fpRef.current.flatpickr.setDate(moment(dateStr).toDate());
                },
              },
              viewButton: {
                text: currentView,
                click: function (e) {
                  setViewMenuVisible(e?.target);
                },
              },
            }}
            initialDate={startDate.format("yyyy-MM-DD")}
            events={events}
            selectable
            editable
            height={700}
            dayMaxEvents={5}
            dateClick={handleDateClick}
            select={handleDateSelect}
            eventClick={handleEventClick}
            eventChange={handleEventChange} // called after events are initialized/added/changed/removed
            /* you can update a remote database when these fire:
      eventAdd={function(){}}
      eventChange={function(){}}
      eventRemove={function(){}}
      */
            scrollTime="08:00:00"
            moreLinkClick={"popover"}
            ref={calRef}
          />
        </SuiBox>
      </SuiBox>
      <ViewActionMenu />
      <ActionMenu />
      {addEventVisible && selectedInfo ? (
        <AddEventModal
          visible={addEventVisible}
          selectInfo={selectedInfo}
          handleClose={() => setAddEventVisible(false)}
          handleRefresh={handleRefresh}
        />
      ) : null}
      {editEventVisible && selectedInfo ? (
        <AddEventModal
          visible={editEventVisible}
          selectInfo={selectedInfo}
          isEdit={true}
          handleClose={() => setEditEventVisible(false)}
          handleRefresh={handleRefresh}
        />
      ) : null}
      {viewEventVisible && selectedInfo ? (
        <EventModalViewer
          visible={viewEventVisible}
          selectInfo={selectedInfo}
          handleOpenEdit={() => {
            setViewEventVisible(false);
            setEditEventVisible(true);
          }}
          handleClose={() => setViewEventVisible(false)}
          handleRefresh={handleRefresh}
        />
      ) : null}
      {createCalendarVisible ? (
        <CreateCalendarModal
          visible={createCalendarVisible}
          handleClose={() => setCreateCalendarVisible(false)}
          handleRefresh={handleRefresh}
          calendarOptions={calendarOptions}
          isEdit={false}
        />
      ) : null}
      {editCalendarVisible && selectedCalendar ? (
        <CreateCalendarModal
          visible={editCalendarVisible}
          calendar={selectedCalendar}
          calendarOptions={calendarOptions}
          isEdit={true}
          handleClose={() => setEditCalendarVisible(false)}
          handleRefresh={handleRefresh}
        />
      ) : null}
      {importCalendarVisible ? (
        <ImportCalendarFileModal
          visible={importCalendarVisible}
          calendarOptions={calendarOptions}
          handleClose={() => setImportCalendarVisible(false)}
          handleRefresh={handleRefresh}
        />
      ) : null}
      {shareToAllDesiignersVisible ? (
        <Prompt
          heading={`Share to all designers`}
          text={`Do you want to share the calendar:${selectedCalendar.label} to all designers!`}
          visible={shareToAllDesiignersVisible}
          okText={"Share"}
          okColor="dark"
          handleOk={async () => {
            try {
              await api.resourcePermission.grantAll({
                resource_id: selectedCalendar.value,
                resource_type: "calendar",
              });
              handleRefresh();
              toast.success(`Share ${selectedCalendar.label} to all designers successfully!`);
            } catch (err) {
              console.log(err);
              toast.error(`Share ${selectedCalendar.label} to all designers failed!`);
            }
            setShareToAllDesiignersVisible(false);
          }}
          handleClose={() => setShareToAllDesiignersVisible(false)}
        />
      ) : null}
      {unshareToAllDesiignersVisible ? (
        <Prompt
          heading={`Unshare to all designers`}
          text={`Do you want to unshare the calendar:${selectedCalendar.label} to all designers!`}
          visible={unshareToAllDesiignersVisible}
          okText={"Unshare"}
          okColor="error"
          handleOk={async () => {
            try {
              await api.resourcePermission.revoke({
                permissions: [
                  {
                    resource_id: selectedCalendar.value,
                    resource_type: "calendar",
                    user_id: "AllDesigners",
                  },
                ],
              });
              handleRefresh();

              toast.success(`Unshare ${selectedCalendar.label} to all designers successfully!`);
            } catch (err) {
              console.log(err);
              toast.error(`Unshare ${selectedCalendar.label} to all designers failed!`);
            }
            setUnshareToAllDesiignersVisible(false);
          }}
          handleClose={() => setUnshareToAllDesiignersVisible(false)}
        />
      ) : null}
    </SuiBox>
  );
};

export default MyCalendarPage;
